import type { Automation } from '@/interfaces/automation'
import type { ActionType, TriggerType } from '@/interfaces/automation'
import { httpClient } from '@/lib/api'
import { AutomationFilters } from '@/types/filters/automation'
import { serializeSearchParams } from '@/utils/serializeSearchParams'

export type ListAutomationsResponse = {
  data: Automation[]
  total: number
  hasMore: boolean
  rowCount: number
}

type CreateAutomationDto = {
  name: string
  description?: string | null
  triggerType: TriggerType
  triggerConfig: Automation['triggerConfig']
  actionType: ActionType
  actionConfig: Automation['actionConfig']
  isActive?: boolean
}

type UpdateAutomationDto = Partial<CreateAutomationDto>

type ToggleAutomationDto = {
  isActive: boolean
}

export class AutomationService {
  public static async list(params: AutomationFilters = {}) {
    const searchParams = new URLSearchParams(serializeSearchParams(params))
    const { data } = await httpClient().get<ListAutomationsResponse>(
      `/automation?${searchParams}`
    )
    return data
  }

  public static async create(payload: CreateAutomationDto) {
    const { data } = await httpClient().post<Automation>(`/automation`, payload)
    return data
  }

  public static async update(id: number, payload: UpdateAutomationDto) {
    const { data } = await httpClient().patch<Automation>(
      `/automation/${id}`,
      payload
    )
    return data
  }

  public static async delete(id: number) {
    const { data } = await httpClient().delete<Automation>(`/automation/${id}`)
    return data
  }

  public static async toggle(id: number, payload: ToggleAutomationDto) {
    const { data } = await httpClient().patch<Automation>(
      `/automation/${id}/toggle`,
      payload
    )
    return data
  }

  public static async duplicate(id: number) {
    const { data } = await httpClient().post<Automation>(
      `/automation/${id}/duplicate`
    )
    return data
  }

  public static async generateWebhookUrl(id: number) {
    const { data } = await httpClient().post<{ url: string }>(
      `/automation/${id}/generate-webhook`
    )
    return data
  }
}
